/**
 * useLegacyLink — composable singleton che:
 *  1. Carica UNA volta /api/v2/legacy-links (cache in memoria per tutta la SPA session).
 *  2. Espone `abilitato` e `legacyUrlFor(routePath)`.
 *
 * Algoritmo di match route → springRoute:
 *  - Cerca il link il cui `springRoute` ha il prefisso più lungo che matcha `routePath`
 *    (es. `/centri/123/edit` matcha `/centri` con prefisso di lunghezza 7).
 *  - Match esatto è caso particolare del prefisso più lungo.
 *  - Il confronto del prefisso richiede che il carattere successivo a `springRoute`
 *    nella routePath sia '/' oppure che le stringhe siano identiche, per evitare
 *    falsi positivi (es. `/centriazienda` non matcha `/centri`).
 *
 * Formato URL legacy (home.seam con parametro pagina):
 *  - `${base}/home.seam?pagina=${encodeURIComponent(pathSenzaSlashIniziale)}`
 *  - Es.: pagina="/pages/strutture/Centri.xhtml" → `/legacy/home.seam?pagina=pages%2Fstrutture%2FCentri.xhtml`
 *
 * Selezione pagina legacy (nessun entitaId — apre sempre la sezione lista):
 *  - Se `pagina` termina con `.xhtml` → usa `pagina`
 *  - Altrimenti se `paginaEdit` termina con `.xhtml` → usa `paginaEdit`
 *  - Altrimenti → null (non mostrare il bottone)
 *
 * Degrada silenziosamente: se l'endpoint fallisce, `abilitato` resta false e
 * `legacyUrlFor` ritorna sempre null.
 */

import { ref, readonly } from 'vue'
import { apiClient } from '@/api/client'

// ── Tipi contratto (tipizzati a mano per il POC; allineare a generated/ quando l'OpenAPI viene aggiornato) ──

export interface LegacyLinkEntry {
  springRoute: string
  pagina?: string
  paginaEdit?: string
}

export interface LegacyLinksResponse {
  abilitato: boolean
  base: string
  links: LegacyLinkEntry[]
}

// ── Cache singleton in-memory ────────────────────────────────────────────────

let _fetchPromise: Promise<void> | null = null
const _abilitato = ref(false)
const _base = ref('/Immigreer')
const _links = ref<LegacyLinkEntry[]>([])

async function _load(): Promise<void> {
  try {
    const { data } = await apiClient.get<LegacyLinksResponse>('/api/v2/legacy-links')
    _abilitato.value = data.abilitato ?? false
    _base.value = data.base ?? '/Immigreer'
    _links.value = Array.isArray(data.links) ? data.links : []
  } catch {
    // Degrada silenziosamente: nessun bottone, nessun errore bloccante
    _abilitato.value = false
    _links.value = []
  }
}

function ensureLoaded(): Promise<void> {
  if (!_fetchPromise) {
    _fetchPromise = _load()
  }
  return _fetchPromise
}

// ── Helper: costruisce l'URL via home.seam?pagina=... ────────────────────────

/**
 * Costruisce l'URL legacy nel formato:
 *   `${base}/home.seam?pagina=${encodeURIComponent(pathSenzaSlashIniziale)}`
 *
 * Lo slash iniziale del path viene rimosso prima di metterlo nel parametro,
 * coerentemente con come le voci di menu legacy passano il valore di `pagina`.
 */
function buildHomeSeamUrl(base: string, xhtmlPath: string): string {
  const normalBase = base.endsWith('/') ? base.slice(0, -1) : base
  // Rimuove lo slash iniziale (es. "/pages/strutture/Centri.xhtml" → "pages/strutture/Centri.xhtml")
  const paginaValue = xhtmlPath.startsWith('/') ? xhtmlPath.slice(1) : xhtmlPath
  return `${normalBase}/home.seam?pagina=${encodeURIComponent(paginaValue)}`
}

function isXhtmlPath(p: string | undefined): p is string {
  return typeof p === 'string' && p.endsWith('.xhtml')
}

// ── Match route → springRoute con prefisso più lungo ─────────────────────────

function findBestMatch(routePath: string, links: LegacyLinkEntry[]): LegacyLinkEntry | null {
  let bestEntry: LegacyLinkEntry | null = null
  let bestLen = -1

  for (const entry of links) {
    const sr = entry.springRoute
    if (!sr) continue

    const isMatch =
      // match esatto
      routePath === sr ||
      // prefisso: il carattere successivo nella routePath deve essere '/'
      (routePath.startsWith(sr) && routePath.length > sr.length && routePath[sr.length] === '/')

    if (isMatch && sr.length > bestLen) {
      bestLen = sr.length
      bestEntry = entry
    }
  }

  return bestEntry
}

// ── Composable pubblico ───────────────────────────────────────────────────────

export function useLegacyLink() {
  // Avvia il caricamento (idempotente)
  ensureLoaded()

  /**
   * Calcola l'URL legacy per la route corrente nel formato home.seam?pagina=...
   *
   * @param routePath  `route.path` della route corrente (es. '/centri' o '/centri/42/edit')
   * @returns          URL completo da aprire in nuova scheda, oppure null se non applicabile
   */
  function legacyUrlFor(routePath: string): string | null {
    if (!_abilitato.value) return null

    const entry = findBestMatch(routePath, _links.value)
    if (!entry) return null

    // Priorità: pagina (lista) prima; paginaEdit come fallback se pagina manca
    if (isXhtmlPath(entry.pagina)) {
      return buildHomeSeamUrl(_base.value, entry.pagina)
    }

    if (isXhtmlPath(entry.paginaEdit)) {
      return buildHomeSeamUrl(_base.value, entry.paginaEdit)
    }

    // né pagina né paginaEdit sono percorsi .xhtml → non supportato nel POC
    return null
  }

  return {
    abilitato: readonly(_abilitato),
    legacyUrlFor,
  }
}
